import {useMemo} from 'react'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {toDisplaySlug} from '../utils/filter-utils'
import {getAgentDisplayName} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'

export interface ResolvedAgentFilter {
  /** Numeric agent IDs to send in the API request */
  resolvedAgentIds: number[] | undefined
  /** Custom agent names (from "custom:" prefix) to send as separate params */
  resolvedCustomAgentNames: string[] | undefined
  /** Whether slug→ID resolution is complete (false while agents are still loading) */
  agentFilterReady: boolean
}

/**
 * Pure function that resolves agent display slugs to numeric IDs.
 * Agent filter values are display-name-derived slugs (e.g. "copilot-coding-agent",
 * "copilot-cli") or custom agent keys ("custom:my-agent"). This function matches
 * them against the agents list by converting each agent's display_name to a slug.
 * Exported for testing; prefer the hook version in components.
 */
export function resolveAgentFilter(
  agentFilter: string[],
  agents: AgentInfo[] | undefined,
  isLoadingAgents: boolean | undefined,
  defaultAgentIds: number[] | undefined,
): ResolvedAgentFilter {
  if (agentFilter.length === 0) {
    return {resolvedAgentIds: defaultAgentIds, resolvedCustomAgentNames: undefined, agentFilterReady: true}
  }

  const customAgentNames = agentFilter.filter(s => s.startsWith('custom:')).map(s => s.slice('custom:'.length))
  const displaySlugs = agentFilter.filter(s => !s.startsWith('custom:'))

  // If we have display slugs but agents haven't loaded yet, we can't resolve them
  if (displaySlugs.length > 0 && isLoadingAgents) {
    return {resolvedAgentIds: defaultAgentIds, resolvedCustomAgentNames: undefined, agentFilterReady: false}
  }

  const resolvedIds: number[] = []
  for (const filterSlug of displaySlugs) {
    // CLI and App share a display label, but requests must retain both real IDs.
    for (const agent of agents ?? []) {
      if (toDisplaySlug(getAgentDisplayName(agent)) === filterSlug && agent.id != null) {
        const numId = Number(agent.id)
        if (Number.isFinite(numId)) resolvedIds.push(numId)
      }
    }
  }

  // The URL has agent slugs but the agents list hasn't loaded yet so we can't
  // resolve them to numeric IDs. Hold off on fetching tasks to avoid briefly
  // showing unfiltered results before the correct filter applies.
  if (displaySlugs.length > 0 && resolvedIds.length === 0 && customAgentNames.length === 0) {
    return {resolvedAgentIds: defaultAgentIds, resolvedCustomAgentNames: undefined, agentFilterReady: false}
  }

  // Combine any default agent IDs (e.g. from group-based filtering) with the
  // IDs resolved from URL slug params, deduplicating in case of overlap.
  const mergedIds = [...new Set([...(defaultAgentIds ?? []), ...resolvedIds])]
  return {
    resolvedAgentIds: mergedIds.length > 0 ? mergedIds : undefined,
    resolvedCustomAgentNames: customAgentNames.length > 0 ? customAgentNames : undefined,
    agentFilterReady: true,
  }
}

/**
 * Resolves agent display slugs from URL params to numeric IDs using the top-level agents data.
 * Display slugs are derived from agent display names (e.g. "copilot-coding-agent" from
 * "Copilot Coding Agent"). Custom agents (prefixed with "custom:") are kept as-is.
 */
export function useResolvedAgentFilter(
  agentFilter: string[],
  agents: AgentInfo[] | undefined,
  isLoadingAgents: boolean | undefined,
  defaultAgentIds: number[] | undefined,
): ResolvedAgentFilter {
  return useMemo(
    () => resolveAgentFilter(agentFilter, agents, isLoadingAgents, defaultAgentIds),
    [agentFilter, agents, isLoadingAgents, defaultAgentIds],
  )
}
